import { graphHooks } from '@tunasong/graph-lib/react'
import { profilesApi, rulesApi } from '@tunasong/redux'
import { ruleFiringBatchSchema } from '@tunasong/schemas'
import type { RuleFiringBatch } from '@tunasong/schemas'
import {
  AutomationsIndex,
  Button,
  describeRule,
  EntityLinkById,
  readRules,
  resumeRule,
  RuleHistory,
  setRuleEnabled,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'

/**
 * `rule-firing-batch` is no longer a registered entity type (Epic 22 moved firing-batch
 * tracking off the entities table entirely into a dedicated firings table, read through
 * `rulesApi.useGetRuleFiringsQuery`). Rows come back as loosely-typed storage records
 * (they carry `hostId`/`sk`/`__expires__` alongside the batch fields), so validate each
 * one against the row schema rather than trusting the response shape -- zod strips the
 * storage-only extras.
 */
const asRuleFiringBatch = (row: Record<string, unknown>): RuleFiringBatch | undefined => {
  const parsed = ruleFiringBatchSchema.safeParse(row)
  return parsed.success ? parsed.data : undefined
}

/**
 * Oversight surface for automations: every rule, plus what the selected one has
 * actually done.
 *
 * Rules are read from the profile because that is the only entity that can host
 * them today. The entity model allows any entity to be a host, and when a second
 * host type ships this enumeration has to change — there is no way to query
 * "every entity carrying rules" without an index.
 */
export const AutomationsPage: FC = () => {
  const { profile, isLoading: profileLoading } = graphHooks.useProfile()
  const [updateProfile] = profilesApi.useUpdateProfileMutation()
  const [selectedRuleId, setSelectedRuleId] = useState<string | undefined>(undefined)

  const config = profile?.config
  const rules = readRules(config)

  // Firing history is fetched on view from the dedicated firings table, not read from
  // the entity graph -- see the module comment on `asRuleFiringBatch`.
  const {
    data: history,
    isFetching: historyFetching,
    isError: historyError,
    refetch: refetchHistory,
  } = rulesApi.useGetRuleFiringsQuery()
  const allBatches = (history?.items ?? []).map(asRuleFiringBatch).filter(Boolean)

  // The index's Last-ran cell only distinguishes "loading" from "known" -- there is no
  // per-row error affordance -- so an error is folded into "loading" there rather than
  // rendered as the false negative `formatLastFired(undefined, …)` would produce
  // ("Hasn't run yet" for a rule the app simply failed to hear back about).
  // isFetching covers both initial load and refetch-after-error (when isLoading is false but a request is in flight).
  const indexHistoryPending = historyFetching || historyError

  const handleToggle = (ruleId: string, enabled: boolean) => {
    if (!profile) {
      return
    }
    void updateProfile({ id: profile.id, profile: { config: setRuleEnabled(config, ruleId, enabled) } })
  }

  const handleResume = (ruleId: string) => {
    if (!profile) {
      return
    }
    void updateProfile({ id: profile.id, profile: { config: resumeRule(config, ruleId) } })
  }

  // "Last ran" comes from the batches: `Rule` has no `lastFiredAt` field.
  const lastFiredByRuleId: Record<string, string> = {}
  for (const batch of allBatches) {
    const current = lastFiredByRuleId[batch.ruleId]
    if (!current || batch.firedAt > current) {
      lastFiredByRuleId[batch.ruleId] = batch.firedAt
    }
  }

  const batches = selectedRuleId ? allBatches.filter(batch => batch.ruleId === selectedRuleId) : []
  const selectedRule = selectedRuleId ? rules.find(rule => rule.id === selectedRuleId) : undefined

  // While the profile itself is still loading, `rules` reads as `[]` -- that is indistinguishable
  // from "you truly have none", so render a neutral loading state instead of asserting the latter.
  if (profileLoading) {
    return (
      <section aria-label="Automations">
        <h1>Automations</h1>
        <p>Loading automations…</p>
      </section>
    )
  }

  // Loading finished but there is still no profile -- an error, not "you have none". `rules` would
  // again read as `[]` here, so without this branch the page would assert the same false negative
  // AutomationsIndex's own empty state already guards against for the loading case.
  if (!profile) {
    return (
      <section aria-label="Automations">
        <h1>Automations</h1>
        <p>Couldn&apos;t load your automations. Try reloading the page.</p>
      </section>
    )
  }

  return (
    <section aria-label="Automations">
      <h1>Automations</h1>
      <Button aria-label="Refresh history" onClick={() => void refetchHistory()}>
        Refresh
      </Button>
      <AutomationsIndex
        rules={rules}
        lastFiredByRuleId={lastFiredByRuleId}
        historyLoading={indexHistoryPending}
        onToggle={handleToggle}
        onSelect={setSelectedRuleId}
        onResume={handleResume}
      />
      {selectedRule ? (
        <>
          <h2>{describeRule(selectedRule).actionLabel}</h2>
          {/* A failed fetch must not read as an eternal "Loading…" -- render the error
           * honestly instead of leaving `RuleHistory` stuck on a loading flag that will
           * never flip to success. */}
          {historyError ? (
            <p>Couldn&apos;t load firing history. Try refreshing.</p>
          ) : (
            <RuleHistory
              batches={batches}
              loading={historyFetching}
              renderEntityLink={entityId => <EntityLinkById entityId={entityId} />}
            />
          )}
        </>
      ) : null}
    </section>
  )
}

export default AutomationsPage
